import { create } from 'zustand';
import { saveAs } from 'file-saver';

import { getReporteExcelCeleryAsync } from '@/shared/actions/erp';
import { getCeleryTaskStatus } from '@/shared/actions/erp/ipv4-groups.actions';
import { useAuthStore } from '@/store/auth/auth.store';

export interface ExcelTaskResult {
  status?: string;
  progress?: number;
  message?: string;
  filename?: string;
  storage_key?: string;
  file_url?: string;
  download_url?: string;
  total_registros?: number;
  [key: string]: unknown;
}

export interface ExcelAsyncTaskStatus {
  task_id?: string;
  status: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
  info?: {
    current?: number;
    total?: number;
    percent?: number;
    status?: string;
    total_registros?: number;
    mensaje?: string;
    [key: string]: unknown;
  };
  result?: ExcelTaskResult;
  error?: string;
}

export async function downloadFileFromUrl(url: string, filename?: string) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    const blob = await response.blob();
    saveAs(blob, filename || url.split('/').pop()?.split('?')[0] || 'reporte.xlsx');
  } catch (error) {
    console.warn('Error descargando blob con fetch, aplicando fallback directo saveAs:', error);
    saveAs(url, filename || url.split('/').pop()?.split('?')[0] || 'reporte.xlsx');
  }
}

type DownloadStatus = 'IDLE' | 'STARTING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE' | 'ERROR';

interface Progress {
  current: number;
  total: number;
  percent: number;
}

interface ExcelDownloadLoaderStore {
  open: boolean;
  taskId: string | null;
  status: DownloadStatus;
  progress: Progress;
  infoStatus: string;
  totalRegistros: number;
  fileUrl: string | null;
  fileName: string | null;
  //initDownload: (params: IDataMovimientoInventarioParams) => Promise<void>;
  initDownload: (path: string, params: any) => Promise<void>;
  pollStatus: () => Promise<void>;
  close: () => void;
  setOpen: (open: boolean) => void;
}

let interval: NodeJS.Timeout | null = null;

function stopPolling() {
  if (interval !== null) {
    clearInterval(interval);
    interval = null;
  }
}

const initialState = {
  open: false,
  taskId: null,
  status: 'IDLE' as DownloadStatus,
  progress: { current: 0, total: 0, percent: 0 },
  infoStatus: '',
  totalRegistros: 0,
  fileUrl: null as string | null,
  fileName: null as string | null,
};

export const useExcelDownloadLoaderStore = create<ExcelDownloadLoaderStore>((set, get) => ({
  ...initialState,

  initDownload: async (path, params) => {
    stopPolling();
    set({ ...initialState, open: true, status: 'STARTING' });

    try {
      const email = useAuthStore.getState().user?.email ?? '';
      const resp = await getReporteExcelCeleryAsync(path, { ...params, email } as any);
      const taskId = (resp.data as { task_id?: string } | undefined)?.task_id;
      if (!taskId) {
        set({ open: true, status: 'ERROR', infoStatus: 'No se recibió task_id del servidor' });
        return;
      }

      set({ taskId, status: 'PROGRESS' });
      get().pollStatus();
      interval = setInterval(() => get().pollStatus(), 10_000);
    } catch {
      set({ open: true, status: 'ERROR', infoStatus: 'Error al iniciar la generación del Excel' });
    }
  },

  pollStatus: async () => {
    const { taskId } = get();
    if (!taskId) return;

    try {
      const response = await getCeleryTaskStatus<ExcelAsyncTaskStatus>(taskId);

      if (response.status === 'SUCCESS') {
        stopPolling();

        const result = response.result as ExcelTaskResult | undefined;
        const fileUrl = result?.file_url || null;
        const fileName = result?.filename || null;
        const message = result?.message || (response.info as any)?.mensaje || 'Excel generado correctamente';
        const totalRegistros = result?.total_registros ?? get().totalRegistros;

        set({
          status: 'SUCCESS',
          progress: { current: 100, total: 100, percent: 100 },
          infoStatus: message,
          totalRegistros,
          fileUrl,
          fileName,
        });

        if (fileUrl) {
          await downloadFileFromUrl(fileUrl, fileName ?? undefined);
        }
        return;
      }

      if (response.status === 'FAILURE') {
        stopPolling();
        set({
          status: 'FAILURE',
          infoStatus: response.error || 'Error en la generación del Excel',
        });
        return;
      }

      const info = response.info;
      if (info) {
        set({
          status: response.status as DownloadStatus,
          progress: {
            current: info.current || 0,
            total: info.total || 0,
            percent: info.percent || 0,
          },
          infoStatus: info.status || '',
          totalRegistros: info.total_registros || 0,
        });
      }
    } catch {
      stopPolling();
      set({ status: 'ERROR', infoStatus: 'Error al consultar el progreso' });
    }
  },

  close: () => {
    stopPolling();
    set({ ...initialState });
  },

  setOpen: (open) => {
    set({ open });
  },
}));
